import type { Container, FederatedPointerEvent, Sprite } from 'pixi.js';
import MathUtils from '../utils/MathUtils';

export class ScrollTapPassthrough {
  private downClick: { x: number; y: number; time: number } | null = null;

  private readonly tapThreshold = {
    time: 150,
    distance: 20,
  };

  constructor(
    inputElement: Sprite,
    private readonly buttonContainer: Container,
  ) {
    inputElement.on('pointerdown', this.onPointerDown);

    inputElement.on('pointerupoutside', this.onPointerUp);
    inputElement.on('pointerup', this.onPointerUp);
  }

  private readonly onPointerUp = (event: FederatedPointerEvent): void => {
    if (!this.downClick) return;

    const distance = MathUtils.getDistance(event.globalX, event.globalY, this.downClick.x, this.downClick.y);
    const timeDiff = Date.now() - this.downClick.time;

    if (distance < this.tapThreshold.distance && timeDiff < this.tapThreshold.time) {
      this.processTap(event);
    }
  };

  private getAllInteractiveElements(root = this.buttonContainer, out: Container[] = []): Container[] {
    if (root.interactive) {
      out.push(root);
    }

    if (root.children && root.interactiveChildren) {
      root.children.forEach((child) => {
        this.getAllInteractiveElements(child, out);
      });
    }

    return out;
  }

  private processTap(event: FederatedPointerEvent): void {
    const container = this.getAllInteractiveElements().find((element) => {
      if (element.hitArea) {
        const localPoint = element.toLocal(event.global);
        return element.hitArea.contains(localPoint.x, localPoint.y);
      }

      return element.getBounds(true).containsPoint(event.globalX, event.globalY);
    });
    if (!container) return;

    if (container.onpointertap) {
      container.onpointertap(event);
    } else {
      container.emit('pointertap', event);
    }
  }

  private readonly onPointerDown = (event: FederatedPointerEvent): void => {
    this.downClick = { x: event.globalX, y: event.globalY, time: Date.now() };
  };
}
